import midnightService from "features/Application/services/midnightService/midnightService";
import {
  beginAction,
  createDraftSuccess,
  failureAction,
  successAction,
  updateBegin,
  updateSuccess,
} from "../slices/campaignEntityStateSlice";
import DraftEntityTypes from "enums/draftEntityTypes";
import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import {
  type LandingPagePayload,
  type CampaignPayload,
  type EmailTemplatePayload,
  type FeedbackPagePayload,
  type FeedbackPageRequest,
  type EmailTemplateRequest,
} from "../../types/models";
import { setIsLoaded } from "../slices/campaignDetailsSlice";
import { beginAsyncOperation } from "features/Application/slices/asyncOperationSlice";
import DataService from "features/Application/services/dataServices/typedDataService";
import {
  CampaignDiscardV2Success,
  CampaignEditV2Success,
  CampaignEmailTemplateEditSuccess,
  CampaignFeedbackPageEditSuccess,
  CampaignLockV2Success,
  CampaignPublishV2Success,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { type DistributedOpUpdateParams, type NotifyStepSettings } from "interfaces";
import azureStorageUtils from "utils/azureStorageUtils";
import { pluralize } from "utils/stringUtils";
import backgroundTask from "features/BackgroundTasks/backgroundTask";

export const createCampaign =
  (payload: CampaignPayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    try {
      const { id, ...createPayload } = payload;
      const result = await midnightService.createLock(DraftEntityTypes.Campaigns, createPayload);
      const entityId = result.data as number;
      dispatch(createDraftSuccess({ entityId }));
      dispatch(setIsLoaded(true));
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const updateCampaign =
  (payload: CampaignPayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(updateBegin());
    dispatch(beginAsyncOperation({ id: payload.id!, action: CampaignEditV2Success }));
    try {
      await DataService.threatDefence.campaigns.updateCampaignAsync(payload);
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const createUpdateEmailTemplateAction = () => {
  let prevHtml: string | undefined;

  return (payload: EmailTemplatePayload): AppThunk => {
    return async (dispatch: AppDispatch) => {
      dispatch(updateBegin());
      dispatch(beginAsyncOperation({ id: payload.id!, action: CampaignEmailTemplateEditSuccess }));
      try {
        const { html, contentUrlWithSas, ...request } = payload;
        await DataService.threatDefence.campaigns.updateEmailTemplateAsync(request as EmailTemplateRequest);

        if (prevHtml !== html) {
          await azureStorageUtils.uploadString(contentUrlWithSas, html);
          prevHtml = html;
        }
      } catch (error) {
        dispatch(failureAction({ error: error as Error }));
      }
    };
  };
};

export const updateLandingPage =
  (payload: LandingPagePayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(updateBegin());
    try {
      await azureStorageUtils.uploadString(payload.contentUrlWithSas, payload.html);
      dispatch(updateSuccess({ entityId: payload.id }));
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const updateFeedbackPage =
  (payload: FeedbackPagePayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(updateBegin());
    dispatch(beginAsyncOperation({ id: payload.id!, action: CampaignFeedbackPageEditSuccess }));
    try {
      const request = getFeedbackPageRequest(payload);
      await DataService.threatDefence.campaigns.updateFeedbackPageAsync(request);
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const lockCampaign =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: CampaignLockV2Success }));
    try {
      await midnightService.getEntityLock(DraftEntityTypes.Campaigns, id);
      successAction({ entityId: id });
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const publishCampaign =
  (id: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: CampaignPublishV2Success }));
    try {
      await midnightService.releaseLock(
        DraftEntityTypes.Campaigns,
        id,
        notificationSettings ? { notificationSettings, notifyTypes } : undefined,
      );
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const discardCampaign =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: CampaignDiscardV2Success }));
    try {
      await midnightService.discardLock(DraftEntityTypes.Campaigns, id);
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const duplicateCampaignsThunk =
  (ids: number[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.threatDefence.campaigns.duplicateCampaigns(ids);
      return data;
    };

    const idsCount: number = ids.length;
    const bannerPrefix = pluralize("Simulated phishing campaign", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DuplicateCampaigns",
      title: `Duplication of campaigns`,
      getOperationProps,
      successTransientMessage: `${bannerPrefix} ${idsCount === 1 ? "was" : "were"} successfully duplicated!`,
      failureTransientMessage: `${bannerPrefix} could not be duplicated.`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const deleteCampaignsThunk =
  (ids: number[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.threatDefence.campaigns.deleteCampaigns(ids);
      return data;
    };

    const idsCount: number = ids.length;
    const bannerPrefix = pluralize("Simulated phishing campaign", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DeleteCampaigns",
      title: `Deletion of campaigns`,
      getOperationProps,
      successTransientMessage: `${bannerPrefix} ${idsCount === 1 ? "was" : "were"} successfully deleted!`,
      failureTransientMessage: `${bannerPrefix} could not be deleted.`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

const getFeedbackPageRequest = (feedbackPage: FeedbackPagePayload): FeedbackPageRequest => ({
  id: feedbackPage.id!,
  videoUrl: feedbackPage.videoUrl,
  videoName: feedbackPage.uploadedVideos?.[0]?.name,
  videoSize: feedbackPage.uploadedVideos?.[0]?.size,
  message: feedbackPage.message,
});
